import { KeyboardEvent, useEffect, useState } from 'react'

import {
  Chip,
  MultiValueTextField,
  Paragraph,
  RadioButton,
  RadioButtonGroup,
  SingleDatePicker,
  Text,
  TextField,
  TextWithLabel,
  ValidationType,
  typography,
} from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import { OrderDiscountType } from '__generated__/atlas-types'

import { ISO_DATE_FORMAT, getDateFromFormat, toServerDate } from 'utils/dates'
import { decimalWithCommas } from 'utils/formatters'

import { getDateFormat } from 'store/currentUser/selectors'

import { PaymentType } from '../SendPaymentRequestModal'
import { PaymentAmount } from '../sendPaymentRequest.hooks'
import { TOOLTIP_INFO_TEXT } from './DetailsTab'
import { isValidDate } from 'features/Orders/Review/utils/review.utils'
import { NumericFormatInput } from 'features/Orders/components/NumericFormatInput/NumericFormatInput'
import { sendPaymentRequestIds } from 'features/Orders/orders.ids'

const ContentWrapper = styled.div`
  margin-top: 30px;
`

const StyledEmailLinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledCCLinkLabel = styled.span`
  ${typography.alpha.primaryActive.body3}
  color: var(--color-primary-900);
`

const StyledCaption = styled.span`
  ${typography.alpha.primaryActive.caption}
  color: var(--color-primary-900);
  &.error {
    color: var(--color-error-500);
  }
`

const PaymentAmountWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 30px;
  > :first-child {
    width: 100%;
  }
`

const PartialAmountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-left: 28px;
  max-width: 60%;
`

const StyledRadioButtonGroup = styled(RadioButtonGroup)`
  margin-left: 16px;

  > * {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`

const forceFixedHeight = css`
  .DayPicker_transitionContainer {
    height: 284px !important;
  }
`

type DetailsPage1Props = {
  orderEmail?: string
  emailChips?: Chip[]
  setEmailChips?: (emailChips: Chip[]) => void
  onFieldError?: (inInvalid: boolean) => void
  remainingAmount?: string
  currency?: string
  paymentName: string
  setPaymentName: (paymentName: string) => void
  paymentAmount: {
    amount: string
    format: OrderDiscountType
  }
  dueDate?: string
  setDueDate: (dueDate: string) => void
  setPaymentAmount: (paymentAmount: PaymentAmount) => void
  paymentType: PaymentType
  setPaymentType: (paymentType: PaymentType) => void
  onResetPayment: () => void
  setNumericInputFilled?: (value: string) => void
}

const DetailsPage1 = ({
  orderEmail,
  emailChips = [],
  setEmailChips = () => {},
  onFieldError = () => {},
  remainingAmount,
  currency,
  paymentName,
  setPaymentName,
  paymentAmount,
  setPaymentAmount,
  dueDate,
  setDueDate,
  paymentType,
  setPaymentType,
  onResetPayment,
  setNumericInputFilled = () => {},
}: DetailsPage1Props) => {
  // used only for display purposes
  const userDateFormat = useSelector(getDateFormat) as string
  const { sendPaymentRequestDueDate } = useFlags()
  const [isEmailFieldInvalid, setIsEmailFieldInvalid] = useState(false)
  const [dateErrorMessage, setDateErrorMessage] = useState('')

  const handleAddEmail = (newChip: Chip) => {
    if (emailChips.some(({ value }) => value === newChip.value)) return
    setEmailChips([...emailChips, newChip])
  }

  const handleRemoveEmail = (chipToDelete: Chip) => {
    setEmailChips(
      emailChips.filter((email) => email.value !== chipToDelete.value),
    )
  }

  const onAmountChange = (evt: { target: HTMLInputElement }) => {
    const value = evt?.target?.value
    setPaymentAmount({
      amount: value ? `${value}` : '0',
      format: paymentAmount.format,
    })
  }

  const handlePaymentNameKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.code === 'Space' && paymentName.length === 0) {
      event.preventDefault()
    }
  }

  const handleDueDateChange = (date?: Date) => {
    if (date && isValidDate(date)) {
      const dateString = toServerDate(date)
      setDueDate(dateString)
      setDateErrorMessage('')
      return false
    } else {
      setDueDate('')
      return true
    }
  }

  const handleFocusOut = (evt: Event) => {
    if (dueDate) return

    const datePicker = evt.target as HTMLInputElement
    if (datePicker && datePicker.value) {
      const dateValue = datePicker.value as string
      const possibleDate = getDateFromFormat(
        dateValue,
        userDateFormat?.toUpperCase() || 'M/D/YYYY',
        { strict: true },
      )
      const invalidDate = handleDueDateChange(possibleDate || undefined)
      setDateErrorMessage(invalidDate ? 'Invalid date' : '')
    } else if (!dueDate) {
      setDateErrorMessage('Invalid date')
    }
  }

  useEffect(() => {
    const datePicker = document.getElementById(
      'send-payment-request-modal-single-date-picker',
    )

    if (datePicker) {
      // We need to attach an event for when the user is leaving the input
      // to validate the date and update the state in case is valid
      datePicker.addEventListener('focusout', handleFocusOut)
    }
    return () => {
      if (datePicker) {
        datePicker.removeEventListener('focusout', handleFocusOut)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ContentWrapper>
      <p>1. Specify any amount up to the remaining balance:</p>
      <StyledRadioButtonGroup>
        <RadioButton
          id={sendPaymentRequestIds.radioButton}
          name="select_payment_type"
          label={`Remaining balance: ${decimalWithCommas(
            remainingAmount || '0',
          )} ${currency || ''}`}
          fontWeight="medium"
          value={PaymentType.FULL}
          checked={paymentType === PaymentType.FULL}
          onChange={() => {
            setPaymentType(PaymentType.FULL)
            onResetPayment()
          }}
        />
        <RadioButton
          id="pay_partial_amount"
          name="select_payment_type"
          label="Partial Amount:"
          fontWeight="medium"
          value={PaymentType.PARTIAL}
          checked={paymentType === PaymentType.PARTIAL}
          onChange={() => {
            setPaymentType(PaymentType.PARTIAL)
          }}
        />
        <PartialAmountWrapper>
          <TextField
            caption="50 character limit."
            isRequired={true}
            label="Payment name"
            placeholder="i.e. Deposit"
            disabled={paymentType === PaymentType.FULL}
            value={paymentName}
            maxLength={50}
            onChange={(e) => {
              setPaymentName(e.target.value)
            }}
            onKeyPress={handlePaymentNameKeyDown}
          />
          <PaymentAmountWrapper>
            <NumericFormatInput
              title="Payment Amount"
              isRequired={true}
              value={paymentAmount.amount}
              handleOnBlur={(amount: { target: HTMLInputElement }) =>
                onAmountChange(amount)
              }
              selectedFormat={paymentAmount.format}
              setSelectedFormat={(format: OrderDiscountType) => {
                setPaymentAmount({
                  amount: '0',
                  format: format,
                })
                setNumericInputFilled('')
              }}
              maxAmount={remainingAmount}
              disabled={paymentType === PaymentType.FULL}
              onNotifyChange={setNumericInputFilled}
            />
            {paymentAmount.format === OrderDiscountType.NUMERIC && (
              <TextWithLabel label="Currency" caption={TOOLTIP_INFO_TEXT}>
                <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                  {currency}
                </Text>
              </TextWithLabel>
            )}
          </PaymentAmountWrapper>
        </PartialAmountWrapper>
      </StyledRadioButtonGroup>
      {sendPaymentRequestDueDate && (
        <>
          <p>2. Add due date to the request:</p>
          <div style={{ width: '200px' }}>
            <Paragraph typeface="montserrat" type="3" margin="0 0 5px 0">
              Due Date{' '}
              <Text as="span" color="error-700">
                *
              </Text>
            </Paragraph>
            {/* we're purposefully avoiding fixing the issue about
              validating types entered manually. several issues in
              react-dates complain about the same issue and the fix
              requires adding a dom event listener which i dont want
              to do here. if someone complains we do it then
             */}
            <SingleDatePicker
              id="send-payment-request-modal-single-date-picker"
              focused={false}
              onDateChange={handleDueDateChange}
              onFocusChange={() => {}}
              // dueDate is stored in the db in ISO_DATE_FORMAT
              date={getDateFromFormat(dueDate || '', ISO_DATE_FORMAT)}
              placeholder={userDateFormat?.toUpperCase()}
              openDirection="up"
              isOutsideRange={(date) => !isValidDate(date)}
              displayFormat={userDateFormat?.toUpperCase()}
              errorMessage={dateErrorMessage}
              // We need to force a fixed height with CSS because the default 'up' position is causing
              // the calendar picker to grow upwards when switching months.
              // With this CSS override, it will stay always with the same height.
              styles={forceFixedHeight}
            />
          </div>
        </>
      )}
      <p>
        {sendPaymentRequestDueDate ? '3. ' : '2. '}Add additional recipients:
      </p>
      <StyledEmailLinkWrapper>
        <StyledCCLinkLabel>BCC link to:</StyledCCLinkLabel>
        <MultiValueTextField
          id={sendPaymentRequestIds.bccEmailField}
          errorMessage="Invalid Email Address"
          placeholder={
            emailChips.length
              ? ''
              : 'Press enter, space, or comma to save email'
          }
          value={emailChips}
          disabled={!orderEmail}
          onAddChip={handleAddEmail}
          onCloseChip={handleRemoveEmail}
          validation={ValidationType.Email}
          onValidate={(isValid) => {
            setIsEmailFieldInvalid(!isValid)
            onFieldError(!isValid)
          }}
        />
        <StyledCaption className={isEmailFieldInvalid ? 'error' : ''}>
          250 Characters Max
        </StyledCaption>
      </StyledEmailLinkWrapper>
    </ContentWrapper>
  )
}

export default DetailsPage1
